import { getTranslations } from "next-intl/server"
import Markdown from "react-markdown"

import { DescriptionExcerpt } from "@/components/marketplace/pdp/description-excerpt"
import { plainDescription } from "@/platform/seo/description"

interface PdpDetailsProps {
  highlights?: string[]
  description?: string | null
  className?: string
}

const HIGHLIGHT_LEAD = /^([^:\n]{2,48}):[ \t]+(\S.*)$/

const splitHighlight = (highlight: string): { lead?: string; body: string } => {
  const [, lead, body] = highlight.match(HIGHLIGHT_LEAD) ?? []
  return lead && body ? { lead: lead.trim(), body: body.trim() } : { body: highlight }
}

// Synced product descriptions carry stray HTML break tags that react-markdown
// escapes into a literal "<br />", and they put each spec/benefit on its own line —
// but Markdown folds a lone newline into a space, so "Característica: … / Beneficio: …"
// blocks collapse into one run-on paragraph. Fold every break variant to a newline,
// bold the "Término:" lead-in so the enumerations read as the list they were authored
// as, then keep every authored line break (a single newline becomes a hard break;
// blank lines stay paragraph breaks). Descriptions with no line breaks are untouched.
const TERM_LINE = /^([^:\n]{2,48}):[ \t]+(\S.*)$/
const markdownFromDescription = (description: string): string =>
  description
    .replace(/<br\s*\/?>/gi, "\n")
    .split("\n")
    .map((line) => {
      const [, term, definition] = line.match(TERM_LINE) ?? []
      return term && definition ? `**${term.trim()}:** ${definition.trim()}` : line
    })
    .join("\n")
    .replace(/([^\n])\n(?!\n)/g, "$1  \n")

const CLAMP_MIN_CHARS = 90

const COPY = "font-text text-copy @3xl:text-copy-lg text-fg-heading"

export const PdpDetails = async ({ highlights, description, className }: PdpDetailsProps) => {
  const t = await getTranslations("pdp")
  const hasHighlights = Boolean(highlights?.length)
  if (!hasHighlights && !description) return null
  const preview = description ? plainDescription(description) : ""
  const full = description && (
    <div className={`${COPY} flex flex-col gap-200`}>
      <Markdown>{markdownFromDescription(description)}</Markdown>
    </div>
  )
  return (
    <section data-testid="pdp-description" className={`flex flex-col gap-125 ${className ?? ""}`}>
      {hasHighlights && (
        <ul data-testid="pdp-highlights" className={`${COPY} flex flex-col gap-125`}>
          {highlights?.map((highlight) => {
            const { lead, body } = splitHighlight(highlight)
            return (
              <li key={highlight}>
                {lead ? (
                  <>
                    <span className="font-medium">{lead}:</span> {body}
                  </>
                ) : (
                  highlight
                )}
              </li>
            )
          })}
        </ul>
      )}
      {description &&
        (preview.length > CLAMP_MIN_CHARS ? (
          <DescriptionExcerpt preview={preview} moreLabel={t("descriptionSeeMore")} className={COPY}>
            {full}
          </DescriptionExcerpt>
        ) : (
          full
        ))}
    </section>
  )
}
